import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimPriceLevelEnum } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimActsKC2Gql } from '../../BimWorkOrderPriceTable/bimWorkOrderPriceQueries';
import type { BimActKc2DataExtended } from '../types';

const requestId = 'bimActsKC2';

export const useBimActsKc2 = (
    workOrderPriceId: number,
    priceLevel: BimPriceLevelEnum | undefined,
    isVersionPage?: boolean,
) => {
    const dispatch = useDispatch();

    const variables = useMemo(
        () => ({
            workOrderPriceId,
            priceLevel,
        }),
        [priceLevel, workOrderPriceId],
    );

    const [resetBimActsKc2Data] = useResetStoreData({
        requestId,
    });

    const [bimActsKc2Data, bimActsKc2Status, , bimActsKc2MakeRequest] = useRequest({
        requestId,
        query: bimActsKC2Gql,
        withEffect: !!workOrderPriceId && !isVersionPage,
        variables,
    });

    useEffect(
        () => () => {
            resetBimActsKc2Data(undefined);
        },
        [resetBimActsKc2Data],
    );

    const refresh = useCallback(() => {
        dispatch(bimActsKc2MakeRequest(variables));
    }, [bimActsKc2MakeRequest, dispatch, variables]);

    const data = useMemo(
        () =>
            bimActsKc2Data?.bimActsKC2?.items?.reduce((acc: BimActKc2DataExtended[], item) => {
                acc.push({ ...item, isActRow: true });

                return acc;
            }, []) ?? [],
        [bimActsKc2Data?.bimActsKC2?.items],
    );

    const isLoading = bimActsKc2Status === RequestStatus.FETCHING;

    return { data, isLoading, refresh };
};
